import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Eye, EyeOff, Plus, X, Download } from 'lucide-react'

const isPwaInstalled =
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as unknown as { standalone?: boolean }).standalone === true
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { accountsApi, authApi, settingsApi } from '@/api'
import { useAuthStore } from '@/store/auth.store'
import { useUiStore } from '@/store/ui.store'
import { useUpload, usePushNotifications } from '@/hooks'
import { Avatar } from '@/components/profile/Avatar'
import { MediaPickerSheet, AddressAutocompleteInput, SectionCard } from '@/components/common'
import { MAX_BIO_LENGTH, MAX_LINKS, MAX_ADDRESS_LENGTH } from '@olimhub/shared'
import { DISPLAY_VERSION } from '@/lib'
import type { NotificationPreferences, GeocodedPoint } from '@olimhub/shared'

// ─── Input helpers ───────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 'var(--radius-md)',
  border: '1.5px solid var(--border)',
  background: 'var(--bg-sunk)',
  color: 'var(--text-strong)',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--fs-body)',
  boxSizing: 'border-box',
  outline: 'none',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: 'var(--font-sans)',
  fontWeight: 600,
  fontSize: 'var(--fs-sm)',
  color: 'var(--text-muted)',
  marginBottom: 6,
}

const primaryBtnStyle: React.CSSProperties = {
  padding: '10px 20px',
  borderRadius: 'var(--radius-pill)',
  border: 'none',
  background: 'var(--brand)',
  color: 'var(--on-brand)',
  fontFamily: 'var(--font-sans)',
  fontWeight: 600,
  fontSize: 'var(--fs-sm)',
  cursor: 'pointer',
}

const dangerBtnStyle: React.CSSProperties = {
  padding: '10px 20px',
  borderRadius: 'var(--radius-pill)',
  border: 'none',
  background: 'var(--danger)',
  color: '#fff',
  fontFamily: 'var(--font-sans)',
  fontWeight: 600,
  fontSize: 'var(--fs-sm)',
  cursor: 'pointer',
}

const outlineBtnStyle: React.CSSProperties = {
  padding: '10px 20px',
  borderRadius: 'var(--radius-pill)',
  border: '1.5px solid var(--border-strong)',
  background: 'transparent',
  color: 'var(--text-strong)',
  fontFamily: 'var(--font-sans)',
  fontWeight: 600,
  fontSize: 'var(--fs-sm)',
  cursor: 'pointer',
}

// ─── EditProfile section ─────────────────────────────────────────────────────

function EditProfileSection() {
  const { t } = useTranslation()
  const { account, updateAccount } = useAuthStore()
  const queryClient = useQueryClient()
  const [displayName, setDisplayName] = useState(account?.displayName ?? '')
  const [about, setAbout] = useState(account?.about ?? '')
  // Prefilled with the resolved city, not the original typed address (never
  // stored) — editing means retyping the address, kept simple on purpose.
  const [address, setAddress] = useState(account?.city ?? '')
  // Set when the user picks a Google-resolved suggestion (see
  // AddressAutocompleteInput's onSelect) — lets save skip the redundant
  // server-side geocode call. Cleared automatically if the address is
  // edited after picking (AddressAutocompleteInput's stale-pick guard).
  const [resolvedPoint, setResolvedPoint] = useState<GeocodedPoint | null>(null)
  const [links, setLinks] = useState<Array<{ label: string; url: string }>>(
    account?.links.map((l) => ({ label: l.label, url: l.url })) ?? [],
  )
  const [newAvatarUrl, setNewAvatarUrl] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const { state: uploadState, error: uploadError, upload: uploadFile } = useUpload('avatar')
  const isUploading = uploadState === 'compressing' || uploadState === 'uploading'
  const [isSaving, setIsSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const avatarUrl = newAvatarUrl ?? account?.avatarUrl ?? null

  async function handleFileSelected(file: File) {
    const url = await uploadFile(file)
    if (url) setNewAvatarUrl(url)
  }

  function addLink() {
    if (links.length >= MAX_LINKS) return
    setLinks([...links, { label: '', url: '' }])
  }

  function removeLink(i: number) {
    setLinks(links.filter((_, idx) => idx !== i))
  }

  function updateLink(i: number, field: 'label' | 'url', value: string) {
    setLinks(links.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)))
  }

  async function handleSave() {
    if (!displayName.trim()) return
    setIsSaving(true)
    setSaved(false)
    setSaveError(null)
    try {
      const updated = await accountsApi.updateMe({
        displayName: displayName.trim(),
        about: about.trim() || undefined,
        address: address.trim() || null,
        ...(resolvedPoint && { latitude: resolvedPoint.latitude, longitude: resolvedPoint.longitude, city: resolvedPoint.city }),
        avatarUrl: avatarUrl ?? undefined,
        links: links.filter((l) => l.label.trim()),
      })
      updateAccount(updated)
      // Deliberately leave the address field showing what the user just
      // typed/picked, rather than snapping it to the resolved city right
      // after save — that read as "my address got cleared" even though it
      // saved correctly. The city-only prefill still happens naturally next
      // time this page mounts (see the useState above), which is all the
      // earlier "keep it simple" decision actually required.
      void queryClient.invalidateQueries({ queryKey: ['profile', updated.username] })
      void queryClient.invalidateQueries({ queryKey: ['profile-feed'] })
      void queryClient.invalidateQueries({ queryKey: ['feed'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      setSaveError(status === 422 ? t('settings.address_not_recognized') : t('settings.save_error'))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <SectionCard title={t('profile.edit_profile')}>
      {/* Avatar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
        <Avatar
          name={account?.displayName || account?.username || ''}
          avatarUrl={avatarUrl}
          animalAvatar={account?.animalAvatar}
          size="lg"
        />
        <div>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            disabled={isUploading}
            style={{ ...outlineBtnStyle, opacity: isUploading ? 0.6 : 1 }}
          >
            {uploadState === 'compressing' ? t('upload.optimizing') : uploadState === 'uploading' ? t('upload.uploading_short') : t('settings.change_photo')}
          </button>
          <MediaPickerSheet
            open={pickerOpen}
            onClose={() => setPickerOpen(false)}
            onFileSelected={handleFileSelected}
            capture="user"
          />
          {uploadError && (
            <p style={{ color: 'var(--danger)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', marginTop: 6, marginBottom: 0 }}>
              {uploadError}
            </p>
          )}
        </div>
      </div>

      {/* Display name */}
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{t('settings.display_name')}</label>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={64}
          style={inputStyle}
        />
      </div>

      {/* About */}
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>
          {t('settings.about')}
          <span style={{ marginInlineStart: 8, fontWeight: 400, color: 'var(--text-faint)' }}>
            {about.length}/{MAX_BIO_LENGTH}
          </span>
        </label>
        <textarea
          value={about}
          onChange={(e) => setAbout(e.target.value)}
          maxLength={MAX_BIO_LENGTH}
          rows={4}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </div>

      {/* Location (opt-in, geocoded — see Profile Geolocation) */}
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{t('settings.location')}</label>
        <AddressAutocompleteInput
          value={address}
          onChange={setAddress}
          onSelect={(_, resolved) => setResolvedPoint(resolved ?? null)}
          maxLength={MAX_ADDRESS_LENGTH}
          placeholder={t('settings.location_placeholder')}
          style={inputStyle}
        />
        <p style={{ margin: '6px 0 0', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-faint)' }}>
          {t('settings.location_help')}
        </p>
      </div>

      {/* Links */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <label style={{ ...labelStyle, marginBottom: 0 }}>{t('settings.links')}</label>
          {links.length < MAX_LINKS && (
            <button
              type="button"
              onClick={addLink}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                color: 'var(--brand)',
                fontFamily: 'var(--font-sans)',
                fontWeight: 600,
                fontSize: 'var(--fs-sm)',
                padding: 0,
              }}
            >
              <Plus size={14} /> {t('settings.add_link')}
            </button>
          )}
        </div>
        {links.map((link, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Label"
              value={link.label}
              onChange={(e) => updateLink(i, 'label', e.target.value)}
              style={{ ...inputStyle, flex: '0 0 120px' }}
            />
            <input
              type="url"
              placeholder="https://…"
              value={link.url}
              onChange={(e) => updateLink(i, 'url', e.target.value)}
              style={{ ...inputStyle, flex: 1 }}
            />
            <button
              type="button"
              onClick={() => removeLink(i)}
              aria-label="Remove link"
              style={{
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                color: 'var(--text-muted)',
                flexShrink: 0,
              }}
            >
              <X size={16} />
            </button>
          </div>
        ))}
      </div>

      {saveError && (
        <p style={{ color: 'var(--danger)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', marginBottom: 12, marginTop: 0 }}>
          {saveError}
        </p>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button type="button" onClick={handleSave} disabled={isSaving} style={{ ...primaryBtnStyle, opacity: isSaving ? 0.6 : 1 }}>
          {isSaving ? t('common.saving') : t('settings.save_changes')}
        </button>
        {saved && (
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--success)', fontWeight: 600 }}>
            {t('settings.save_success')}
          </span>
        )}
      </div>
    </SectionCard>
  )
}

// ─── ChangePassword section ───────────────────────────────────────────────────

function ChangePasswordSection() {
  const { t } = useTranslation()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNew, setShowNew] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSuccess(false)
    if (newPassword !== confirmPassword) {
      setError(t('settings.password_mismatch'))
      return
    }
    if (newPassword.length < 8) {
      setError(t('settings.password_too_short'))
      return
    }
    setIsSaving(true)
    try {
      await authApi.passwordChange({ currentPassword, newPassword })
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setSuccess(true)
      setTimeout(() => setSuccess(false), 4000)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to change password.'
      setError(msg)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <SectionCard title={t('settings.change_password')}>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>{t('settings.current_password')}</label>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>{t('settings.new_password')}</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              style={{ ...inputStyle, paddingInlineEnd: 44 }}
            />
            <button
              type="button"
              onClick={() => setShowNew((v) => !v)}
              style={{
                position: 'absolute',
                insetInlineEnd: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                color: 'var(--text-muted)',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>{t('settings.confirm_new_password')}</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            style={inputStyle}
          />
        </div>

        {error && (
          <p style={{ color: 'var(--danger)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', marginBottom: 12, marginTop: 0 }}>
            {error}
          </p>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button type="submit" disabled={isSaving} style={{ ...primaryBtnStyle, opacity: isSaving ? 0.6 : 1 }}>
            {isSaving ? t('common.saving') : t('settings.change_password')}
          </button>
          {success && (
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--success)', fontWeight: 600 }}>
              {t('settings.password_changed')}
            </span>
          )}
        </div>
      </form>
    </SectionCard>
  )
}

// ─── Preferences section ──────────────────────────────────────────────────────

const UI_LANGS = ['en', 'ru', 'pt', 'es'] as const
type UiLang = typeof UI_LANGS[number]

function PreferencesSection() {
  const { theme, toggleTheme } = useUiStore()
  const { t, i18n } = useTranslation()

  const currentLang = i18n.language.slice(0, 2) as UiLang

  function handleLangChange(lang: UiLang) {
    i18n.changeLanguage(lang)
    document.documentElement.lang = lang
    document.documentElement.dir = 'ltr'
  }

  return (
    <SectionCard title={t('settings.preferences')}>
      {/* Dark mode */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body)', color: 'var(--text-body)' }}>
          {t('settings.dark_mode')}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={theme === 'dark'}
          onClick={toggleTheme}
          style={{
            width: 44,
            height: 24,
            borderRadius: 'var(--radius-pill)',
            border: 'none',
            background: theme === 'dark' ? 'var(--brand)' : 'var(--border-strong)',
            position: 'relative',
            cursor: 'pointer',
            transition: 'background var(--dur-fast) var(--ease-out)',
            flexShrink: 0,
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: 3,
              insetInlineStart: theme === 'dark' ? 23 : 3,
              width: 18,
              height: 18,
              borderRadius: '50%',
              background: '#fff',
              transition: 'inset-inline-start var(--dur-fast) var(--ease-out)',
            }}
          />
        </button>
      </div>

      {/* Interface language */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body)', color: 'var(--text-body)', fontWeight: 500 }}>
            {t('settings.interface_language')}
          </div>
          <div style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', marginTop: 2 }}>
            {t('settings.interface_language_hint')}
          </div>
        </div>
        <div
          role="group"
          style={{ display: 'inline-flex', padding: 3, gap: 2, background: 'var(--bg-sunk)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)' }}
        >
          {UI_LANGS.map((lang) => {
            const on = currentLang === lang
            return (
              <button
                key={lang}
                type="button"
                onClick={() => handleLangChange(lang)}
                aria-pressed={on}
                style={{
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: 'var(--radius-pill)',
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 700,
                  fontSize: 'var(--fs-xs)',
                  letterSpacing: '0.02em',
                  background: on ? 'var(--brand)' : 'transparent',
                  color: on ? 'var(--on-brand)' : 'var(--text-muted)',
                }}
              >
                {lang.toUpperCase()}
              </button>
            )
          })}
        </div>
      </div>
    </SectionCard>
  )
}

// ─── Notifications section ───────────────────────────────────────────────────

function Toggle({ on, onChange, disabled }: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={() => onChange(!on)}
      style={{
        width: 44,
        height: 24,
        borderRadius: 'var(--radius-pill)',
        border: 'none',
        background: on ? 'var(--brand)' : 'var(--border-strong)',
        position: 'relative',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'background var(--dur-fast) var(--ease-out)',
        flexShrink: 0,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span style={{
        position: 'absolute',
        top: 3,
        insetInlineStart: on ? 23 : 3,
        width: 18,
        height: 18,
        borderRadius: '50%',
        background: '#fff',
        transition: 'inset-inline-start var(--dur-fast) var(--ease-out)',
      }} />
    </button>
  )
}

function PrefRow({ label, on, onChange, disabled }: { label: string; on: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body)', color: 'var(--text-body)' }}>{label}</span>
      <Toggle on={on} onChange={onChange} disabled={disabled} />
    </div>
  )
}

function NotificationsSection() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { permission, isSubscribed, isLoading: pushLoading, subscribe, unsubscribe } = usePushNotifications()

  const { data: prefs } = useQuery({
    queryKey: ['settings', 'notifications'],
    queryFn: () => settingsApi.getNotificationPreferences(),
  })

  const patchMutation = useMutation({
    mutationFn: (patch: Partial<NotificationPreferences>) => settingsApi.patchNotificationPreferences(patch),
    onSuccess: (updated) => {
      queryClient.setQueryData(['settings', 'notifications'], updated)
    },
  })

  function patch(key: keyof NotificationPreferences, value: boolean) {
    patchMutation.mutate({ [key]: value })
  }

  const notSupported = !('serviceWorker' in navigator) || !('PushManager' in window)

  return (
    <SectionCard title={t('settings.push_notifications')}>
      {notSupported ? (
        <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', margin: 0 }}>
          Push notifications are not supported in this browser.
        </p>
      ) : permission === 'denied' ? (
        <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', margin: 0 }}>
          {t('settings.push_denied')}
        </p>
      ) : (
        <>
          <PrefRow
            label={t('settings.push_this_device')}
            on={isSubscribed}
            onChange={(v) => (v ? subscribe() : unsubscribe())}
            disabled={pushLoading}
          />
          {prefs && (
            <div style={{ marginTop: 4, opacity: !isSubscribed ? 0.45 : 1, pointerEvents: !isSubscribed ? 'none' : 'auto' }}>
              <PrefRow label={t('settings.push_on_follow')} on={prefs.pushOnFollow} onChange={(v) => patch('pushOnFollow', v)} />
              <PrefRow label={t('settings.push_on_mention')} on={prefs.pushOnMention} onChange={(v) => patch('pushOnMention', v)} />
              <PrefRow label={t('settings.push_on_reply')} on={prefs.pushOnReply} onChange={(v) => patch('pushOnReply', v)} />
              <PrefRow label={t('settings.push_on_boost')} on={prefs.pushOnBoost} onChange={(v) => patch('pushOnBoost', v)} />
              <PrefRow label={t('settings.push_on_dm')} on={prefs.pushOnDm} onChange={(v) => patch('pushOnDm', v)} />
              <PrefRow label={t('settings.push_on_claps_digest')} on={prefs.pushOnClapsDigest} onChange={(v) => patch('pushOnClapsDigest', v)} />
            </div>
          )}
        </>
      )}
    </SectionCard>
  )
}

// ─── App updates section ──────────────────────────────────────────────────────

// Manual escape hatch for anyone still stuck on a service worker from before
// sw.ts unconditionally called self.skipWaiting() — that version never
// checked for updates on its own, so uninstalling/reinstalling the PWA was
// the only way out. This forces the same registration.update() the
// background poller (src/lib/swUpdate.ts) does, then reloads once the new
// worker actually takes control (not just once it's installed).
function AppUpdateSection() {
  const { t } = useTranslation()
  const [status, setStatus] = useState<'idle' | 'checking' | 'up-to-date' | 'updating'>('idle')

  if (!('serviceWorker' in navigator)) return null

  async function handleCheck() {
    setStatus('checking')
    const registration = await navigator.serviceWorker.getRegistration().catch(() => undefined)
    if (!registration) {
      setStatus('idle')
      return
    }

    await registration.update().catch(() => undefined)
    // Check state directly rather than listening for "updatefound" — verified
    // empirically that the event can still be pending when update()'s promise
    // resolves, while registration.installing/.waiting are already set by then.
    const updateFound = !!(registration.installing || registration.waiting)

    if (!updateFound) {
      setStatus('up-to-date')
      setTimeout(() => setStatus('idle'), 2500)
      return
    }

    setStatus('updating')
    navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), { once: true })
    // Fallback in case controllerchange never fires (e.g. the fetched worker
    // still errors before activating) — reload anyway rather than leaving
    // the button stuck on "Updating…". Long enough for a real network to
    // finish fetching+precaching the ~2MB precache manifest before this
    // fires and reloads under the *previous* worker: an 8s version of this
    // reliably fired first on production, so "Check for Updates" would
    // reload one version behind the one it just found (confirmed on a real
    // deploy, not just localhost, where the download finishes near-instantly).
    setTimeout(() => window.location.reload(), 30_000)
  }

  const busy = status === 'checking' || status === 'updating'

  return (
    <SectionCard title={t('settings.app_updates')}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
          {status === 'up-to-date' ? t('settings.app_up_to_date')
            : status === 'updating' ? t('settings.app_updating')
            : t('settings.app_updates_hint')}
        </span>
        <button
          type="button"
          onClick={() => { void handleCheck() }}
          disabled={busy}
          style={{
            padding: '8px 16px',
            borderRadius: 'var(--radius-pill)',
            border: 'none',
            background: 'var(--brand)',
            color: 'var(--on-brand)',
            fontFamily: 'var(--font-sans)',
            fontWeight: 700,
            fontSize: 'var(--fs-sm)',
            cursor: busy ? 'default' : 'pointer',
            opacity: busy ? 0.7 : 1,
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {status === 'checking' ? t('settings.checking_for_updates') : t('settings.check_for_updates')}
        </button>
      </div>
      <div style={{ marginTop: 8, fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-faint)' }}>
        {t('settings.app_version', { version: DISPLAY_VERSION })}
      </div>
    </SectionCard>
  )
}

// ─── DeleteAccount section ────────────────────────────────────────────────────

function DeleteAccountSection() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { clearAuth } = useAuthStore()
  const [expanded, setExpanded] = useState(false)
  const [password, setPassword] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleConfirmDelete() {
    if (!password) return
    setIsDeleting(true)
    setError(null)
    try {
      await authApi.deleteAccount({ password })
      clearAuth()
      navigate('/')
    } catch {
      setError(t('settings.delete_error'))
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <SectionCard title={t('settings.danger_zone')} danger>
      {!expanded ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          style={dangerBtnStyle}
        >
          {t('settings.delete_account')}
        </button>
      ) : (
        <div>
          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-sm)',
              color: 'var(--text-body)',
              marginBottom: 16,
              marginTop: 0,
              lineHeight: 'var(--lh-normal)',
            }}
          >
            {t('settings.delete_account_warning')}
          </p>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>{t('settings.your_password')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              style={inputStyle}
            />
          </div>

          {error && (
            <p style={{ color: 'var(--danger)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', marginBottom: 12, marginTop: 0 }}>
              {error}
            </p>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              type="button"
              onClick={handleConfirmDelete}
              disabled={isDeleting || !password}
              style={{ ...dangerBtnStyle, opacity: isDeleting || !password ? 0.6 : 1 }}
            >
              {isDeleting ? t('settings.deleting') : t('settings.confirm_delete')}
            </button>
            <button
              type="button"
              onClick={() => { setExpanded(false); setPassword(''); setError(null) }}
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--fs-sm)',
                color: 'var(--text-muted)',
                padding: 0,
              }}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </SectionCard>
  )
}

// ─── SettingsPage ─────────────────────────────────────────────────────────────

export function SettingsPage() {
  const { t } = useTranslation()
  return (
    <div
      style={{
        maxWidth: 'var(--feed-max)',
        margin: '0 auto',
        padding: '16px 16px 80px',
      }}
    >
      {!isPwaInstalled && (
        <Link
          to="/how-to-install"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: 'var(--brand)',
            fontFamily: 'var(--font-sans)',
            fontWeight: 600,
            fontSize: 'var(--fs-sm)',
            textDecoration: 'none',
            marginBottom: 16,
          }}
        >
          <Download size={16} strokeWidth={2} />
          {t('settings.how_to_install_title')}
        </Link>
      )}
      <EditProfileSection />
      <ChangePasswordSection />
      <PreferencesSection />
      <NotificationsSection />
      <AppUpdateSection />
      <DeleteAccountSection />
    </div>
  )
}
